import { useCustomViewBlockContext } from '@lighthouse/block'
import { AudioBlock } from '@lighthouse/block/src/blocks'
import type { ActorEvent, AudioBlockAbstract } from '@lighthouse/core'
import { getFileTypeByUrl, getImageFullUrlInApplication, isTextValue } from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId } from '@/hooks/useApplication'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface AudioBlockControllerProps extends React.ComponentPropsWithoutRef<'div'> {
    blockData: AudioBlockAbstract
}

const AudioBlockController: React.FunctionComponent<AudioBlockControllerProps> = ({ blockData, ...rest }) => {
    const {
        config: { audioSource }
    } = blockData
    const appId = useCurrentAppId()

    const { record: viewRecord } = useCustomViewBlockContext()
    const { handleActionListTrigger } = useActionTrigger(blockData.id)
    const handleTriggerEvent = useCallback(
        (id: string, actorEvent: ActorEvent) => {
            return handleActionListTrigger(id, actorEvent, { viewRecord })
        },
        [handleActionListTrigger, viewRecord]
    )
    const richtextDeps = useMemo(() => [audioSource].filter(Boolean), [audioSource])
    const parseVariable = useParseVariableWithSubscription(richtextDeps)

    const value = useMemo(() => {
        if (!audioSource) {
            return ''
        }

        const cellValue = parseVariable(audioSource, { viewRecord })
        if (!cellValue) {
            return ''
        }
        if (cellValue.type === 'file' || cellValue.type === 'video') {
            const audioValue = cellValue.value?.find(v => getFileTypeByUrl(v) === 'audio')
            return audioValue ? getImageFullUrlInApplication(appId, audioValue) || '' : ''
        }
        const otherValue = cellValue?.value
        if (otherValue && isTextValue(otherValue)) {
            return otherValue
        }
        return ''
    }, [appId, audioSource, parseVariable, viewRecord])

    return <AudioBlock blockData={blockData} value={value} onTriggerEvent={handleTriggerEvent} {...rest} />
}

export default AudioBlockController
